import { useMemo } from "react";
import LazyLoad from "react-lazyload";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  TablePagination,
} from "@mui/material";

import AggregateResultsHeader from "../pricing-reports/AggregateResultsHeader";
import { statisticCardContainerSx } from "../pricing-reports/constants";
import FlyerGalleryCard from "./FlyerGalleryCard";

export default function FlyerGallery({
  displayItems = [],
  serverPagination,
  isLoading = false,
  isRefreshing = false,
  error = null,
  onCardClick,
}) {
  const totalCount = serverPagination?.totalCount ?? 0;

  const cardCells = useMemo(
    () =>
      (displayItems || []).map((displayItem) => {
        const thumbnailUrl = displayItem?.images?.[0] ?? "";
        return (
          <Grid key={displayItem.id} item xs={6} sm={6} md={3} lg={3} xl={3}>
            <Box sx={{ height: "100%" }}>
              <LazyLoad height="100%">
                <FlyerGalleryCard
                  displayItem={displayItem}
                  thumbnailUrl={thumbnailUrl}
                  onCardClick={onCardClick}
                />
              </LazyLoad>
            </Box>
          </Grid>
        );
      }),
    [displayItems, onCardClick]
  );

  const showInitialLoading = isLoading && !displayItems.length && !error;
  const showEmptyState = !isLoading && !error && totalCount === 0;

  return (
    <Box sx={{ p: 2, ...statisticCardContainerSx }}>
      <AggregateResultsHeader
        title="Flyer gallery"
        subtitle="Promotional price history rows for the current filters."
      />

      {error ? (
        <Alert severity="error" sx={{ mb: isRefreshing ? 1 : 0 }}>
          {error.message || "Something went wrong while loading the flyer gallery."}
        </Alert>
      ) : null}

      {isRefreshing ? (
        <LinearProgress sx={{ mb: 1 }} aria-label="Loading flyer gallery" />
      ) : null}

      {showInitialLoading ? (
        <Stack alignItems="center" py={3}>
          <CircularProgress size={28} aria-label="Loading flyer gallery" />
        </Stack>
      ) : null}

      {showEmptyState ? (
        <Alert severity="info">No promotions for the current filters.</Alert>
      ) : null}

      {!showInitialLoading && !showEmptyState ? (
        <>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              {cardCells}
            </Grid>
          </Box>
          <TablePagination
            component="div"
            count={totalCount}
            page={serverPagination.page}
            rowsPerPage={serverPagination.rowsPerPage}
            onPageChange={serverPagination.onPageChange}
            onRowsPerPageChange={serverPagination.onRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </>
      ) : null}
    </Box>
  );
}
